import throttle from "lodash/throttle";
import { useEffect, useId, useMemo } from "react";

import { Impulse } from "~/tools/impulse";

import { useTimestampCursorState } from "./_context";

export { useTimestampCursorState } from "./_context";

export const SET_TIMESTAMP_MARKER_HOTKEY = "Shift";
export const SET_CURSOR_THROTTLE_MS = 15;

/**
 * A hook that returns the current timestamp stored in TimestampCursorProvider.
 * Each of the hook calls defines a source of the timestamp, so it can distinguish between its own and other sources.
 * It is required because representation of the timestamp cursor is different for the source and other sources, ex:
 * when a user hovers over the global timeline, a dark solid line will be displayed for the hovered timestamp,
 * but when a user hovers anything else, the global timeline will display a dashed line for the hovered timestamp.
 *
 * @returns an Impulse storing either a timestamp or undefined if no timestamp is stored or if the timestamp is stored by the same source.
 */
export const useTimestampCursor = (): Impulse<undefined | number> => {
	const sourceId = useId();
	const state = useTimestampCursorState();

	const cursor = useMemo(() => {
		const setter = throttle((timestamp?: number) => {
			if (timestamp == null) {
				state.resetCursor();
			} else {
				state.setCursor(timestamp, sourceId);
			}
		}, SET_CURSOR_THROTTLE_MS);

		const derived = Impulse((scope) => state.getCursor(scope, sourceId), setter);

		return { derived, setter };
	}, [state, sourceId]);

	useEffect(
		() => () => {
			cursor.setter.cancel();
		},
		[cursor.setter],
	);

	return cursor.derived;
};
